"use client";

import React, { useEffect, useState } from "react";
import axios from "axios";
import Select from "react-select";
import { X } from "lucide-react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { adminBaseUrl } from "../../utils/baseUrl";
import apiClient from "../../utils/apiClient";

const categories = ["Engineering", "Planning & Scheduling", "Construction", "Others"];
const categoriesChinese = ["工程", "規劃與排程", "施工", "其他"];

const categoryOptionsEn = categories.map(c => ({ value: c, label: c }));
const categoryOptionsZh = categoriesChinese.map(c => ({ value: c, label: c }));

// ✅ Quill toolbar configuration
const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ align: [] }],
    ["link"],
    ["clean"],
  ],
};

const quillFormats = [
  "header",
  "bold",
  "italic",
  "underline",
  "strike",
  "list",
  "bullet",
  "align",
  "link",
];

export default function ProjectsSectionAdmin() {
  const [projects, setProjects] = useState([]);
  const [newProject, setNewProject] = useState({
    title: "",
    englishCategory: [],
    chineseCategory: [],
    englishDescription: "",
    chineseDescription: "",
    completionYear: "",
  });
  const [editingProject, setEditingProject] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingProjects, setProjectsLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const res = await apiClient.get(`${adminBaseUrl}/project/get`);
      if (res.data.success) setProjects(res.data.projects);

      setProjectsLoading(false)
    } catch (err) {
      console.error("Error fetching projects", err);
    }
  };

  const handleAddChange = (e) => {
    const { name, value } = e.target;
    setNewProject(prev => ({ ...prev, [name]: value }));
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditingProject(prev => ({ ...prev, [name]: value }));
  };

  const handleAddSelectChange = (name, selectedOptions) => {
    setNewProject(prev => ({ ...prev, [name]: selectedOptions.map(opt => opt.value) }));
  };

  const handleEditSelectChange = (name, selectedOptions) => {
    setEditingProject(prev => ({ ...prev, [name]: selectedOptions.map(opt => opt.value) }));
  };

  const handleAddProject = async () => {
    const { title, englishCategory, chineseCategory } = newProject;
    if (!title || englishCategory.length === 0 || chineseCategory.length === 0) {
      alert("Please fill all required fields.");
      return;
    }

    try {
      setLoading(true);
      const res = await apiClient.post(`${adminBaseUrl}/project/add`, newProject);
      setNewProject({
        title: "",
        englishCategory: [],
        chineseCategory: [],
        englishDescription: "",
        chineseDescription: "",
        completionYear: "",
      });
      setModalOpen(false);
      fetchProjects();

    } catch (err) {
      console.error("Add failed", err);
    } finally {
      setLoading(false);
    }
  };

  const handleEditProject = async () => {
    if (!editingProject) return;
    const { id, title, englishCategory, chineseCategory } = editingProject;
    if (!title || englishCategory.length === 0 || chineseCategory.length === 0) {
      alert("Please fill all required fields.");
      return;
    }

    try {
      setLoading(true);
      const res = await apiClient.put(`${adminBaseUrl}/project/update/${id}`, editingProject);
      setEditModalOpen(false);
      setEditingProject(null);
      fetchProjects();

    } catch (err) {
      console.error("Edit failed", err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await apiClient.delete(`${adminBaseUrl}/project/delete/${id}`);
      fetchProjects();
    } catch (err) {
      console.error("Delete failed", err);
    }
  };

  return (
    <div className="p-6 mx-auto ml-[280px] h-[90%] overflow-y-scroll ">

      {
        loadingProjects ? 'Loading Projects...' : (
          <>
            <div className="flex justify-between items-center mb-6">
              <h1 className="text-2xl font-bold text-gray-800">Projects Management</h1>
              <button
                onClick={() => setModalOpen(true)}
                className="bg-gradient-to-r from-amber-400 to-amber-600 text-white px-4 py-2 rounded-lg shadow hover:shadow-lg"
              >
                + Add Project
              </button>
            </div>

            {/* Project Table */}
            <div className="overflow-x-auto border rounded-xl shadow-sm">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-100 text-gray-700 uppercase text-xs">
                  <tr>
                    <th className="p-3 text-left">Title</th>
                    <th className="p-3 text-left">English Category</th>
                    <th className="p-3 text-left">Chinese Category</th>
                    <th className="p-3 text-left">Year</th>
                    <th className="p-3 text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {projects.map((proj) => (
                    <tr key={proj.id} className="border-t">
                      <td className="p-3 font-semibold">{proj.title}</td>
                      <td className="p-3 text-gray-600">{proj.englishCategory?.join(", ")}</td>
                      <td className="p-3 text-gray-600">{proj.chineseCategory?.join(", ")}</td>
                      <td className="p-3">{proj.completionYear}</td>
                      <td className="p-3 flex gap-2">
                        <button
                          onClick={() => {
                            setEditingProject(proj);
                            setEditModalOpen(true);
                          }}
                          className="text-blue-600 font-semibold hover:underline"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(proj.id)}
                          className="text-red-600 font-semibold hover:underline"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                  {projects.length === 0 && (
                    <tr>
                      <td colSpan="5" className="text-center text-gray-400 py-6">
                        No projects found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

          </>
        )
      }
      {/* Add Modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-xl w-[95%] max-w-2xl relative overflow-y-auto max-h-[90vh]">
            <button onClick={() => setModalOpen(false)} className="absolute right-3 top-3 text-gray-500">
              <X className="w-5 h-5" />
            </button>
            <h2 className="text-xl font-semibold mb-4">Add Project</h2>

            <input
              name="title"
              placeholder="Title"
              value={newProject.title}
              onChange={handleAddChange}
              className="w-full border p-2 rounded mb-3"
            />

            <label>English Categories</label>
            <Select
              isMulti
              options={categoryOptionsEn}
              value={categoryOptionsEn.filter(opt => newProject.englishCategory.includes(opt.value))}
              onChange={(selected) => handleAddSelectChange("englishCategory", selected)}
              className="mb-3"
            />

            <label>Chinese Categories</label>
            <Select
              isMulti
              options={categoryOptionsZh}
              value={categoryOptionsZh.filter(opt => newProject.chineseCategory.includes(opt.value))}
              onChange={(selected) => handleAddSelectChange("chineseCategory", selected)}
              className="mb-3"
            />

            <label>English Description</label>
            <ReactQuill
              theme="snow"
              modules={quillModules}
              formats={quillFormats}
              value={newProject.englishDescription}
              onChange={(value) => setNewProject(prev => ({ ...prev, englishDescription: value }))}
              className="mb-3"
            />

            <label>Chinese Description</label>
            <ReactQuill
              theme="snow"
              modules={quillModules}
              formats={quillFormats}
              value={newProject.chineseDescription}
              onChange={(value) => setNewProject(prev => ({ ...prev, chineseDescription: value }))}
              className="mb-3"
            />

            <input
              name="completionYear"
              placeholder="Year of Completion"
              value={newProject.completionYear}
              onChange={handleAddChange}
              className="w-full border p-2 rounded mb-4"
            />

            <button
              onClick={handleAddProject}
              disabled={loading}
              className="w-full bg-gradient-to-r from-amber-400 to-amber-600 text-white py-2 rounded-lg font-semibold shadow hover:shadow-lg"
            >
              {loading ? "Saving..." : "Add Project"}
            </button>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editModalOpen && editingProject && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-xl w-[95%] max-w-2xl relative overflow-y-auto max-h-[90vh]">
            <button onClick={() => setEditModalOpen(false)} className="absolute right-3 top-3 text-gray-500">
              <X className="w-5 h-5" />
            </button>
            <h2 className="text-xl font-semibold mb-4">Edit Project</h2>

            <input
              name="title"
              value={editingProject.title}
              onChange={handleEditChange}
              className="w-full border p-2 rounded mb-3"
            />

            <label>English Categories</label>
            <Select
              isMulti
              options={categoryOptionsEn}
              value={categoryOptionsEn.filter(opt => editingProject.englishCategory?.includes(opt.value))}
              onChange={(selected) => handleEditSelectChange("englishCategory", selected)}
              className="mb-3"
            />

            <label>Chinese Categories</label>
            <Select
              isMulti
              options={categoryOptionsZh}
              value={categoryOptionsZh.filter(opt => editingProject.chineseCategory?.includes(opt.value))}
              onChange={(selected) => handleEditSelectChange("chineseCategory", selected)}
              className="mb-3"
            />

            <label>English Description</label>
            <ReactQuill
              theme="snow"
              modules={quillModules}
              formats={quillFormats}
              value={editingProject.englishDescription}
              onChange={(value) =>
                setEditingProject(prev => ({ ...prev, englishDescription: value }))
              }
              className="mb-3"
            />

            <label>Chinese Description</label>
            <ReactQuill
              theme="snow"
              modules={quillModules}
              formats={quillFormats}
              value={editingProject.chineseDescription}
              onChange={(value) =>
                setEditingProject(prev => ({ ...prev, chineseDescription: value }))
              }
              className="mb-3"
            />

            <input
              name="completionYear"
              value={editingProject.completionYear}
              onChange={handleEditChange}
              className="w-full border p-2 rounded mb-4"
            />

            <button
              onClick={handleEditProject}
              disabled={loading}
              className="w-full bg-gradient-to-r from-amber-400 to-amber-600 text-white py-2 rounded-lg font-semibold shadow hover:shadow-lg"
            >
              {loading ? "Updating..." : "Save Changes"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
